/**
 * Notifications API client (packages/protocol/src/api-insights.ts + the
 * notifyserver additions: lifecycle PATCH, events, slack install-url, rotate-secret).
 * All endpoints take `?org=` so the dashboard can manage any org the user belongs to.
 */
import type { Channel, ChannelType, Delivery, Notification, Rule, RuleSource, Severity } from "@backstory/protocol";
import { deploymentRuleSources } from "@backstory/deployment";
import { request } from "../../api";

export type { Channel, ChannelType, Delivery, Notification, Rule, RuleSource, Severity };

export interface ChannelView extends Channel {
  secretsSet: string[];
}

/** Server rows carry a few fields beyond the protocol types. */
export type DeliveryRow = Omit<Delivery, "status"> & { status: Delivery["status"] | "rate_limited"; kind?: string; title?: string };
export type InboxQuery = { q?: string; state?: string; source?: string; unread?: boolean };
export type NotificationRow = Notification & { assignee?: string; snoozedUntil?: string; targetType?: string; targetId?: string };

export interface InboxItem {
  id: string;
  notification: NotificationRow;
  readAt?: string;
  ackedAt?: string;
  createdAt: string;
}

export interface RuleInput {
  projectId?: string | null;
  name?: string;
  enabled?: boolean;
  source?: RuleSource;
  conditions?: Rule["conditions"];
  channelIds?: string[];
  cooldownMinutes?: number;
  digest?: Rule["digest"];
  quietHours?: Rule["quietHours"];
  escalation?: Rule["escalation"];
  clearDigest?: boolean;
  clearQuietHours?: boolean;
  clearEscalation?: boolean;
}

export interface ChannelInput {
  projectId?: string;
  type?: ChannelType;
  name?: string;
  enabled?: boolean;
  config?: Record<string, unknown>;
  secrets?: Record<string, string>;
  minimalPayload?: boolean;
}

export interface TestResult {
  ok: boolean;
  responseCode?: number;
  ref?: string;
  error?: string;
  at: string;
}

const enc = encodeURIComponent;
const org = (o: string, extra = "") => `?org=${enc(o)}${extra}`;

export const notificationsApi = {
  rules: (o: string) => request<Rule[]>(`/v1/notifications/rules${org(o)}`),
  createRule: (o: string, body: RuleInput) => request<Rule>(`/v1/notifications/rules${org(o)}`, { method: "POST", body }),
  patchRule: (o: string, id: string, body: RuleInput) => request<Rule>(`/v1/notifications/rules/${enc(id)}${org(o)}`, { method: "PATCH", body }),
  deleteRule: (o: string, id: string) => request<void>(`/v1/notifications/rules/${enc(id)}${org(o)}`, { method: "DELETE" }),

  channels: (o: string) => request<ChannelView[]>(`/v1/notifications/channels${org(o)}`),
  createChannel: (o: string, body: ChannelInput) => request<{ channel: ChannelView; revealedSecrets?: Record<string, string> }>(`/v1/notifications/channels${org(o)}`, { method: "POST", body }),
  patchChannel: (o: string, id: string, body: ChannelInput) => request<ChannelView>(`/v1/notifications/channels/${enc(id)}${org(o)}`, { method: "PATCH", body }),
  deleteChannel: (o: string, id: string) => request<void>(`/v1/notifications/channels/${enc(id)}${org(o)}`, { method: "DELETE" }),
  testChannel: (o: string, id: string) => request<TestResult>(`/v1/notifications/channels/${enc(id)}/test${org(o)}`, { method: "POST" }),
  rotateSecret: (o: string, id: string) => request<{ secret: string; previousValidUntil: string }>(`/v1/notifications/channels/${enc(id)}/rotate-secret${org(o)}`, { method: "POST" }),
  slackConversations: (o: string, id: string) => request<{ channels: Array<{ id: string; name: string }> }>(`/v1/notifications/channels/${enc(id)}/slack/conversations${org(o)}`),
  slackOrgConversations: (o: string) => request<{ channels: Array<{ id: string; name: string }>; team?: string }>(`/v1/notifications/slack/conversations${org(o)}`),
  slackInstallUrl: (o: string) => request<{ configured: boolean; url: string }>(`/v1/notifications/slack/install-url${org(o)}`),

  deliveries: (o: string, q: { channelId?: string; status?: string; cursor?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (q.channelId) p.set("channelId", q.channelId);
    if (q.status) p.set("status", q.status);
    if (q.cursor) p.set("cursor", q.cursor);
    if (q.limit) p.set("limit", String(q.limit));
    const s = p.toString();
    return request<{ items: DeliveryRow[]; nextCursor?: string }>(`/v1/notifications/deliveries${org(o, s ? "&" + s : "")}`);
  },
  replayDelivery: (o: string, id: string) => request<DeliveryRow>(`/v1/notifications/deliveries/${enc(id)}/replay${org(o)}`, { method: "POST" }),

  notifications: (o: string, q: { state?: string; cursor?: string } = {}) => {
    const p = new URLSearchParams();
    if (q.state) p.set("state", q.state);
    if (q.cursor) p.set("cursor", q.cursor);
    const s = p.toString();
    return request<{ items: NotificationRow[]; nextCursor?: string }>(`/v1/notifications${org(o, s ? "&" + s : "")}`);
  },
  patchNotification: (o: string, id: string, body: { state?: string; assignee?: string; snooze?: "1h" | "24h" | "release"; mute?: boolean }) =>
    request<NotificationRow>(`/v1/notifications/${enc(id)}${org(o)}`, { method: "PATCH", body }),

  inbox: (o: string, q: InboxQuery & { cursor?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (q.q) p.set("q", q.q);
    if (q.state) p.set("state", q.state);
    if (q.source) p.set("source", q.source);
    if (q.unread) p.set("unread", "true");
    if (q.cursor) p.set("cursor", q.cursor);
    if (q.limit) p.set("limit", String(q.limit));
    const s = p.toString();
    return request<{ items: InboxItem[]; unread: number; nextCursor?: string }>(`/v1/inbox${org(o, s ? "&" + s : "")}`);
  },
  inboxItem: (o: string, id: string) => request<InboxItem>(`/v1/inbox/${enc(id)}${org(o)}`),
  ackInbox: (o: string, id: string) => request<{ ok: true }>(`/v1/inbox/${enc(id)}/ack${org(o)}`, { method: "POST" }),
};

export const notifyKeys = {
  rules: (o: string) => ["notify", "rules", o] as const,
  channels: (o: string) => ["notify", "channels", o] as const,
  deliveries: (o: string, q: unknown) => ["notify", "deliveries", o, q] as const,
  /** Prefix for every inbox query; invalidate this after any inbox change. */
  inbox: (o: string) => ["notify", "inbox", o] as const,
  inboxCount: (o: string) => ["notify", "inbox", o, "count"] as const,
  inboxList: (o: string, q: InboxQuery) => ["notify", "inbox", o, "list", q] as const,
  inboxItem: (o: string, id: string) => ["notify", "inbox", o, "item", id] as const,
  notifications: (o: string, q: unknown) => ["notify", "notifications", o, q] as const,
};

// ---------------------------------------------------------------------------
// Static metadata for the UI
// ---------------------------------------------------------------------------

export const SOURCES: Array<{ id: RuleSource; label: string; group: string; hint: string; legacy?: boolean }> = [
  { id: "cluster.created", label: "New issue cluster", group: "Issues", hint: "A new cluster of Signals appeared (rage clicks, dead clicks, errors…)." },
  { id: "cluster.regressed", label: "Issue regressed", group: "Issues", hint: "Hourly count exceeds the 7-day baseline by the regression factor." },
  { id: "cluster.threshold", label: "Issue crossed a threshold", group: "Issues", hint: "Count or affected users crossed the configured threshold." },
  { id: "cluster.reopened", label: "Issue reopened", group: "Issues", hint: "A resolved cluster came back." },
  // Existing rules remain editable, but the rule form hides these legacy
  // sources for new rules so a JS exception cannot notify twice.
  { id: "error.new", label: "New error (legacy)", group: "Legacy", hint: "Use New issue cluster with the JS error kind instead.", legacy: true },
  { id: "error.rate", label: "Error rate spike (legacy)", group: "Legacy", hint: "Use Issue crossed a threshold with the JS error kind instead.", legacy: true },
  { id: "release.regression", label: "Release regression", group: "Releases", hint: "Error rate after a deploy exceeds the previous release." },
  { id: "vitals.regression", label: "Web Vitals regression", group: "Performance", hint: "LCP, INP, or CLS regressed against the 7-day baseline." },
  { id: "monitor.trigger", label: "Monitor-mode trigger", group: "Kiosks & monitoring", hint: "A kiosk or telehealth visit fired a trigger (feed lost, disconnect, custom)." },
  { id: "ai.digest", label: "AI digest ready", group: "AI", hint: "Daily or weekly digest generated." },
  { id: "ai.anomaly", label: "AI anomaly", group: "AI", hint: "Statistical anomaly detected and explained." },
  { id: "ai.deep_analysis_ready", label: "Deep analysis finished", group: "AI", hint: "A requested Explain / repro / PR draft is ready." },
  { id: "live.help_requested", label: "Live Assist: help requested", group: "Live Assist", hint: "An end user asked for help from the in-app widget." },
  { id: "live.control_started", label: "Live Assist: remote control started", group: "Live Assist", hint: "An agent began controlling a user's page." },
  { id: "usage.threshold", label: "Usage threshold", group: "Account", hint: "A meter reached 80% or 100% of the plan." },
  { id: "billing.event", label: "Billing event", group: "Account", hint: "Invoice paid or failed, plan changed." },
  { id: "security.break_glass", label: "Security: break-glass login", group: "Security & privacy", hint: "An owner bypassed Require SSO." },
  { id: "security.sso_changed", label: "Security: SSO changed", group: "Security & privacy", hint: "SSO connection created, changed, or removed." },
  { id: "security.token_created", label: "Security: API token created", group: "Security & privacy", hint: "A new API token was issued." },
  ...deploymentRuleSources,
  { id: "custom", label: "Custom event", group: "Custom", hint: "A Backstory.track() event with the given name and properties." },
];

export const SEVERITIES: Severity[] = ["info", "low", "medium", "high", "critical"];

export const CHANNEL_TYPES: Array<{ id: ChannelType; label: string; description: string }> = [
  { id: "email", label: "Email", description: "Send to specific addresses, or to every owner and admin." },
  { id: "slack", label: "Slack", description: "Post to a channel. Install the Backstory app, or paste a bot token." },
  { id: "teams", label: "Microsoft Teams", description: "Adaptive Card to a channel Incoming Webhook." },
  { id: "webhook", label: "Webhook", description: "Signed JSON to your endpoint. Verify the HMAC secret." },
  { id: "jira", label: "Jira Cloud", description: "Open one issue. Closing it resolves the notification." },
  { id: "github", label: "GitHub Issues", description: "Open one issue. Closing it resolves the notification." },
  { id: "azuredevops", label: "Azure DevOps", description: "Open one work item. Closing it resolves the notification." },
  { id: "pagerduty", label: "PagerDuty", description: "Page the person on call." },
  { id: "opsgenie", label: "Opsgenie", description: "Open an alert for the person on call." },
  { id: "linear", label: "Linear", description: "Open one issue. Closing it resolves the notification." },
  { id: "inbox", label: "In-app inbox", description: "Every member sees it in the dashboard inbox." },
];

export function channelTypeLabel(t: ChannelType): string {
  return CHANNEL_TYPES.find((x) => x.id === t)?.label ?? t;
}

export const SIGNAL_KINDS = [
  "rage_click", "dead_click", "error_click", "thrashed_cursor", "form_abandonment", "field_struggle", "failed_request", "slow_request", "js_error",
  "frozen_ui", "layout_instability", "slow_page", "navigation_loop", "back_button_bounce", "blank_page", "broken_resource", "console_error_burst",
  "offline_period", "crash", "accessibility_trap", "consent_friction", "monitor_trigger", "custom",
  "backend_exception", "backend_error_log", "metric_threshold",
] as const;

/** Signature verification examples live in the docs, not the dashboard. */
export const WEBHOOK_DOCS_URL = "https://docs.backstory.io/api/webhooks/#signature";
